import React, { Fragment } from "react";
import { lazyWithRetry } from "../../core/lazy_with_retry";
import { useQueryString } from "../../hooks/use_query_string";

import { useI18n } from "../../lib/i18n/i18n_provider";
import { GeneralInformation } from "../template_offering_details_base";
import { TemplateOfferingDetailsDayOffice } from "../template_offering_details_day_office";
import { Text } from "../text";
import { TextLink } from "../text_link";
import { useWeWorkAnalytics } from "./wework_analytics";
import { useWeWorkPartnerConfig } from "./wework_partner_config";

const Theme = lazyWithRetry(() => import("./wework_theme"));

export const faqsEN = [
  {
    question: "When can we access the WeWork building?",
    answer:
      "Access to the building is granted between 9 AM until 5:30 PM. Please notice accessing the building is not possible before 9 AM.",
  },
  {
    question: "How do we access the office?",
    answer:
      "When you and your team arrive at the building, just check in with a member of our community team. If needed, they will get you set up with a keycard – just don’t forget your ID, they will need to check that first!",
  },
  {
    question: "How do I modify or cancel a booking?",
    answer: (
      <Text>
        To modify your reservation, please email{" "}
        <TextLink href="mailto:help@flexspace.ai">
          help@flexspace.ai{". "}
        </TextLink>
        Our cancellation policy allows you to cancel your reservation 24 hours
        before the reservation begins (until 9:00 the day before your
        reservation).
      </Text>
    ),
  },
  {
    question: "How many people can access the office?",
    answer:
      "You may invite as many team members as the office capacity allows.",
  },
  {
    question: "Is this a shared office or a private office?",
    answer: "Our daily offices are all private, with a lockable door.",
  },
  {
    question:
      "I made a reservation for several days, can I stay beyond the business hours?",
    answer:
      "No, the access hours are the same when booking a single day or multiple days (9:00 until 17:30.)",
  },
  {
    question:
      "Can I leave my equipment at the office if I’m staying for more than one day?",
    answer:
      "You may leave your gear at the office only after signing a waiver provided by the building’s Community team.",
  },
  {
    question:
      "Can I send food/equipment delivery to the WeWork building before I arrive?",
    answer:
      "Only the host and its guests are allowed to bring in items to the office. The on site stuff cannot accept any type of delivery.",
  },
  {
    question: "Can I use the printer?",
    answer: "Printing is not included in Daily Office booking.",
  },
  {
    question: "What furniture is in the office?",
    answer:
      "Every office is equipped with an ergonomic chair, office desk, and a drawer as the number of the office’s capacity. Also in the office, you’ll find ethernet and power outlets adjacent to every desk.",
  },
  {
    question: "Do you provide extension cord and ethernet cables if needed?",
    answer:
      "Of course! Reach out to the community team if you’re in need of one of those.",
  },
];

export const faqsHE = [
  {
    question: "מתי ניתן להיכנס לבניין של WeWork?",
    answer:
      "הגישה לבניין ניתנת מ-09:00 עד 17:30.  אנא שימו לב כי לא ניתן להכנס לבניין לפני השעה 09:00.",
  },
  {
    question: "איך נכנסים למשרד?",
    answer:
      "כאשר אתם והצוות שלכם מגיעים לבניין, גשו לדלפק הכניסה לצוות הקהילה. הצוות ידאג לתת לכם כרטיס מפתח בהתאם למשרד ששריינתם - רק אל תשכחו להביא איתכם תעודה מזהה, זה הדבר הראשון שהצוות יבקש ממכם.",
  },
  {
    question: "איך ניתן לשנות או לבטל את ההזמנה שלי?",
    answer: (
      <Text>
        כדי לשנות את הזמנתכם, אנא שלחו אימייל לכתובת{" "}
        <TextLink href="mailto:help@flexspace.ai">help@flexspace.ai</TextLink>
        {". "}
        ניתן לבטל או לשנות את ההזמנה 24 שעות לפני תחילת ההזמנה שלכם (עד השעה
        09:00 ביום שלפני ההזמנה שלכם).
      </Text>
    ),
  },
  {
    question: "כמה אנשים יכולים לקבל גישה למשרד?",
    answer:
      "מספר האנשים שבאפשרותכם להזמין משתנה בהתאם לכמות המקסימלית שהמשרד שבחרתם מאפשר.",
  },
  {
    question: "האם זה משרד פרטי או משותף?",
    answer: "המשרדים היומיים שלנו כולם פרטיים, עם דלת הניתנת לנעילה.",
  },
  {
    question:
      "הזמנתי משרד לכמה ימים, האם זה אומר שאפשר להישאר במשרד לאחר שעות הפעילות?",
    answer:
      "לא, הגישה למשרד תתאפשר באותן השעות עבור הזמנת משרד ליום אחד או לכמה ימים (09:00-17:30).",
  },
  {
    question: "האם אפשר להשאיר ציוד אישי במשרד אם ביצעתי הזמנה ליותר מיום אחד?",
    answer:
      "באפשרותכם להשאיר ציוד במשרד רק לאחר חתימה על הטופס הרלוונטי שיינתן לכם על ידי צוות הקהילה בבניין.",
  },
  {
    question: "האם אפשר לבצע משלוח של מזון או ציוד ל-WeWork?",
    answer:
      "רק המארח ואורחיו מורשים להכניס מזון או ציוד למשרד. צוות ניהול הקהילה לא יכול לקבל שום משלוח בשמכם.",
  },
  {
    question: "האם יש אפשרות להשתמש במדפסת?",
    answer: "הדפסות אינן כלולות בהזמנת משרד יומי.",
  },
  {
    question: "איזה ריהוט יש במשרד?",
    answer:
      "כל משרד מאובזר בכיסא משרדי, שולחן עבודה וארונית מגירות כמספר עמדות העבודה במשרד. בנוסף, תמצאו חיבור לאינטרנט ושקעי חשמל בצמוד לכל עמדה.",
  },
  {
    question: "האם אתם מספקים כבל רשת במידת הצורך?",
    answer: "בטח! מוזמנים לפנות לצוות הקהילה.",
  },
];

const generalInfoEN: GeneralInformation = {
  title: "General information and cancellation policy",
  description:
    "Here are a few important notes before you book your Daily Office:",
  bulletPoints: [
    {
      point: "Cancellation and changes",
      text: "You may cancel or change the date of your reservation 24 hours before the reservation begins (until 9:00 the day before your reservation).",
    },
    {
      point: "Access hours",
      text: "Access to the building and your office will be granted between 09:00 to 17:30. The access hours stay the same when booking several days together. Please notice that it is not possible to access the building before 9:00.",
    },
    {
      point: "Office amenities",
      text: "Each office is equipped with an ergonomic chair, office desk, and a drawer as the number of the office’s capacity. Also in the office, you’ll find ethernet and power outlets adjacent to every desk. Screens, keyboards and other technical equipment are not included in the office.",
    },
    {
      point: "Office capacity",
      text: "The capacity represents the maximum number of team members that can access the office and the building on the day of the reservation.",
    },
    {
      point: "Conference rooms and guests",
      text: (
        <Text>
          Guests are not allowed if you have booked a daily pass. If you'd like
          to bring a guest, please{" "}
          <TextLink href="/offerings/wework-meeting-rooms">
            book a conference room
          </TextLink>{" "}
          that can accommodate the number of your guests. Guests' names must be
          entered before booking and arriving at the building.
        </Text>
      ),
    },
  ],
};

const generalInfoHE: GeneralInformation = {
  title: "מידע כללי ומדיניות ביטולים",
  description: "כמה דברים שחשוב שתדעו לפני הזמנה של גישה יומית:",
  bulletPoints: [
    {
      point: "ביטולים ושינויים",
      text: "ניתן לשנות או לבטל את הזמנתכם עד 24 שעות לפני מועד ההזמנה (לכל המאוחר עד לשעה 09:00 בבוקר ביום שלפני מועד ההזמנה).",
    },
    {
      point: "שעות פעילות",
      text: "הגישה לבניין תתאפשר בין השעות 09:00 ל-17:00 בימים ראשון עד חמישי. שעות הפעילות זהות גם בהזמנה של מספר ימים ברצף. שימו לב שכניסה לבניין לפני השעה 09:00 איננה אפשרית.",
    },
    {
      point: "עמדת העבודה שלכם",
      text: `כל המשרדים מצויידים במספר כסאות ארגונומים, שולחנות עבודה ושידות משרדיות על פי קיבולת המשרד. ציוד טכני כגון מסכים, ומקלדות לא כלולים במשרדים.`,
    },
    {
      point: "תפוסת המשרד",
      text: "התפוסה מציינת את מספר האנשים המקסימלי שיכולים לגשת למשרד ולבניין ביום ההזמנה.",
    },
    {
      point: "מדיניות אורחים וחדרי ישיבות",
      text: (
        <Text>
          אורחים יכולים להצטרף רק אם הוזמן חדר ישיבות בנוסף למשרד היומי. אם
          ברצונכם להזמין אורחים, אנא{" "}
          <TextLink href="/offerings/wework-meeting-rooms">
            שריינו חדר ישיבות{" "}
          </TextLink>{" "}
          המתאים למספר האורחים שתרצו להזמין.
        </Text>
      ),
    },
  ],
};

const physicalSpaceIDs = [
  "4fffdf04-a443-4e10-816d-16a27d04e86b", // production: Office G-01, WeWork Azrieli Town
  "ff5c3fc4-9932-45ff-8aea-f0f50b4b9a11", // staging: WeWork Haifa - Office 1
];

interface DayOfficeParams {
  physicalSpaceID: string;
}

export function OfferingWeWorkDayOffice() {
  const { locale } = useI18n();
  useWeWorkAnalytics();
  const partnerConfig = useWeWorkPartnerConfig();
  const queryParams = useQueryString<DayOfficeParams>();

  return (
    <Fragment>
      <Theme />
      <TemplateOfferingDetailsDayOffice
        showAddress
        showDirections
        firstDayOfWeek={0}
        useLegacyImages={true}
        partnerConfig={{
          ...partnerConfig,
          imageForIllustrationOnly: !physicalSpaceIDs.includes(
            queryParams.physicalSpaceID
          ),
        }}
        data={{
          description: `Enjoy wework co-working for the day. You get a desk for the day with all the hot desk member benefits.`,
          faqs: locale.baseLanguageTag === "he" ? faqsHE : faqsEN,
          generalInfo:
            locale.baseLanguageTag === "he" ? generalInfoHE : generalInfoEN,
        }}
      />
    </Fragment>
  );
}
